import {
  addCompany,
  deleteCompany,
  getCompanies,
  updateCompany,
} from "../actions/company.action";
import TableList from "../components/TableList";
import { Company } from "../models/company.model";
import { Button, Link as LinkHref } from "@mui/material";
import { useCallback, useRef } from "react";
import CompanyForm from "./CompanyForm";
import { filterOnKeys, offsetToString } from "../lib/helper";
import { FilterGroup, FilterType } from "../components/TableFilter";
import { FormRefEvent, HeadCell } from "../models/base.model";
import { CheckCircleOutline, HighlightOff } from "@mui/icons-material";
import { green, red } from "@mui/material/colors";
import PreviewImageContainer from "../components/PreviewImageContainer";
import InsertChartIcon from '@mui/icons-material/InsertChart';
import ChartConfigForm from "./ChartConfigForm";
import { upsertChartConfig } from "../actions/company.chart.config.action";
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import SentimentConfigForm from "./SentimentConfigForm";
import { upsertSentimentConfig } from "../actions/sentiment.action";
const headCells: readonly HeadCell[] = [
  {
    id: "name",
    label: "Name",
  },

  {
    id: "email",
    label: "Email",
  },

  {
    id: "logo",
    label: "Logo",
    render: (v) => v && <PreviewImageContainer url={v} isImage height={80} />,
  },

  {
    id: "country",
    label: "Country",
  },

  {
    id: "timezone",
    label: "Timezone",
    render: (v) => offsetToString(v),
  },
  {
    id: "websiteUrl",
    label: "Website Url",
    render: (v) => (
      <LinkHref href={v} target="_blank">
        {v}
      </LinkHref>
    ),
  },
  {
    id: "enabled",
    label: "Enabled",
    render: (value) =>
      value ? (
        <CheckCircleOutline sx={{ color: green[500] }} />
      ) : (
        <HighlightOff sx={{ color: red[500] }} />
      ),
  },
];

const filters: FilterGroup<Company>[] = [
  {
    title: "Filter By Date",
    items: [
      {
        name: "createdAt",
        type: FilterType.date_range,
      },
      {
        name: "updatedAt",
        type: FilterType.date_range,
      },
    ],
  },
];

export default function Companies() {
  const formRef = useRef<FormRefEvent<Company>>(null);
  const chartConfigRef = useRef<FormRefEvent<any>>(null);
  const sentimentConfigRef = useRef<FormRefEvent<any>>(null);

  const onAddOrUpdate = (param?: Company) => {
    return window.webApp.setTab<Company>({
      title: `${param ? "Update" : "New"} Company`,
      element: <CompanyForm ref={formRef} defaultValues={param} />,
      onOk: async () => {
        if (!formRef.current) {
          throw new Error("Form creation error, please refresh or contact us!");
        }
        const value = await formRef.current.onSubmit();
        const res = param
          ? await updateCompany({ ...param, ...value })
          : await addCompany(value);
        return res;
      },
      show: true,
    });
  };

  const onDelete = (param: Company) => {
    return deleteCompany(param.id);
  };

  const onSearch = async (value: string, original: Company[]) => {
    if (original) {
      return {
        data: original.filter((a) => filterOnKeys(a, ["name", "email"], value)),
      };
    } else {
      return { data: [] };
    }
  };

  const renderActions = useCallback((record: Company) => {
    return [
      {
        label: "Charts",
        icon: <InsertChartIcon />,
        onClick: async () => {
          setTimeout(() => {
            window.webApp.setTab<Company>({
              title: "Charts",
              element: <ChartConfigForm companyId={record.id} ref={chartConfigRef} />,
              onOk: async () => {
                const value = await chartConfigRef.current?.onSubmit();
                await upsertChartConfig({
                  companyId: record.id,
                  pages: value?.pages || [],
                });
              },
              show: true,
            });
          }, 100);
        },
      },
      {
        label: "Sentiment",
        icon: <SentimentSatisfiedAltIcon />,
        onClick: async () => {
          setTimeout(() => {
            window.webApp.setTab<Company>({
              title: "Sentiment",
              element: <SentimentConfigForm companyId={record.id} ref={sentimentConfigRef} />,
              onOk: async () => {
                const value = await sentimentConfigRef.current?.onSubmit();
                if (value) {
                  await upsertSentimentConfig(value);
                }
              },
              show: true,
            });
          }, 100);
        },
      },
    ];
  }, []);

  return (
    <TableList<Company>
      headCells={headCells}
      onAddBtnText="New Company"
      uid="id"
      onAdd={onAddOrUpdate}
      onSearch={onSearch}
      searchText="Search by name, email"
      onEdit={onAddOrUpdate}
      onDelete={onDelete}
      onLoad={getCompanies}
      filters={filters}
      actions={renderActions}
    />
  );
}
